import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Renders arbitrary report HTML (as produced by utils/reportRenderer's
 * generateFullReportHtml) into a real, multi-page PDF file — client-side only, no
 * backend/service needed. Standard html2canvas+jsPDF recipe: snapshot the HTML to
 * an image, then slice that image across A4 pages. Tradeoff: PDF text is a
 * rasterized image (not selectable/searchable), the accepted cost of reusing the
 * existing arbitrary-HTML report output without rebuilding it in a PDF-specific
 * renderer (e.g. @react-pdf/renderer).
 */
export async function renderHtmlToPdfBlob(html: string): Promise<Blob> {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '800px'; // fixed width for consistent, predictable page layout
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, { scale: 2 }); // scale=2 for print-quality resolution
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return pdf.output('blob');
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Generates a PDF from report HTML and opens it in a new tab — the browser's own
 * native PDF viewer supplies zoom/print/download controls, so no custom "Download"
 * button needs to be built. `filename` isn't honored as the downloaded file's name
 * with this blob-in-new-tab approach (the browser names it from the blob URL);
 * revisit with `pdf.save(filename)` (direct download, no preview tab) if a named
 * download turns out to matter more than an in-app preview.
 */
export async function openReportAsPdf(html: string, filename: string): Promise<void> {
  void filename;
  const blob = await renderHtmlToPdfBlob(html);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
